import { Injectable, signal } from '@angular/core';

export const SETTINGS_TABS = [
    'profile',
    'security',
    'wallet',
    'workspace',
    'chat-preferences',
    'notifications',
    'theme',
    'connections',
    'skills',
    'usage',
    'usage-limits',
    'web-security',
    'apps-admin',
    'api-keys',
    'single-sign-on',
    'reports',
] as const;

export type SettingsTab = (typeof SETTINGS_TABS)[number];

export function isSettingsTab(value: string): value is SettingsTab {
    return (SETTINGS_TABS as readonly string[]).includes(value);
}

@Injectable({ providedIn: 'root' })
export class SettingsService {
    private readonly _isOpen = signal(false);
    private readonly _activeTab = signal<SettingsTab>('profile');

    readonly isOpen = this._isOpen.asReadonly();
    readonly activeTab = this._activeTab.asReadonly();

    private readonly _focusOwnExtraUsage = signal(false);
    /** Set when the chat banner sends an owner or admin to their own Extra usage row in Usage & Limits. */
    readonly focusOwnExtraUsage = this._focusOwnExtraUsage.asReadonly();

    // ── Developer Mode reveal (ADR-0022) ─────────────────────────────
    //
    // Clicking the Profile tab five times in a row reveals the Developer Mode
    // toggle on the Profile page. A tidiness device, not a security control:
    // the setting grants no authority, so nothing is protected by the gesture.
    //
    // The counter lives here rather than in the dialog component because
    // `open()` is what knows a DIFFERENT tab was clicked, which is what breaks
    // the streak. The reveal is read by ProfileSettings, which already injects
    // this service.
    private static readonly CLICKS_TO_REVEAL = 5;

    private readonly _profileClicks = signal(0);
    private readonly _developerModeRevealed = signal(false);

    readonly developerModeRevealed = this._developerModeRevealed.asReadonly();

    open(tab?: SettingsTab): void {
        if (tab && tab !== 'profile') {
            // Streak broken - the gesture is five clicks IN A ROW on Profile,
            // so idly cycling tabs can never accumulate into a reveal.
            this._profileClicks.set(0);
        }
        if (tab) {
            this._activeTab.set(tab);
        }
        this._isOpen.set(true);
    }

    /** Count one Profile-tab click. Returns clicks remaining before the reveal,
     *  or 0 once revealed - the caller uses it to decide whether to hint. */
    noteProfileTabClick(): number {
        if (this._developerModeRevealed()) {
            return 0;
        }
        const clicks = this._profileClicks() + 1;
        this._profileClicks.set(clicks);
        if (clicks >= SettingsService.CLICKS_TO_REVEAL) {
            this._developerModeRevealed.set(true);
            this._profileClicks.set(0);
            return 0;
        }
        return SettingsService.CLICKS_TO_REVEAL - clicks;
    }

    /** Opens Usage & Limits on the viewer's own Extra usage row (CIQ-2230). */
    openOwnExtraUsage(): void {
        this._focusOwnExtraUsage.set(true);
        this.open('usage-limits');
    }

    clearFocusOwnExtraUsage(): void {
        this._focusOwnExtraUsage.set(false);
    }

    private readonly _focusExtraUsageRequests = signal(false);
    /** Set when an owner or admin selects the "asked for extra usage" notification. */
    readonly focusExtraUsageRequests = this._focusExtraUsageRequests.asReadonly();
    private readonly _extraUsageRequestsVersion = signal(0);
    /** Changes whenever a member asks for extra usage, so an open requests table reloads. */
    readonly extraUsageRequestsVersion = this._extraUsageRequestsVersion.asReadonly();

    /** Opens Usage & Limits on the Extra usage requests table (CIQ-2230). */
    openExtraUsageRequests(): void {
        this._focusExtraUsageRequests.set(true);
        this.open('usage-limits');
    }

    clearFocusExtraUsageRequests(): void {
        this._focusExtraUsageRequests.set(false);
    }

    noteExtraUsageRequested(): void {
        this._extraUsageRequestsVersion.update(v => v + 1);
    }

    private readonly _extraUsageSettingsVersion = signal(0);
    /** Changes whenever an extra usage setting is saved, so the viewer's Usage tab can appear or go. */
    readonly extraUsageSettingsVersion = this._extraUsageSettingsVersion.asReadonly();

    noteExtraUsageChanged(): void {
        this._extraUsageSettingsVersion.update(v => v + 1);
    }

    close(): void {
        this._focusOwnExtraUsage.set(false);
        this._focusExtraUsageRequests.set(false);
        this._isOpen.set(false);
        this._activeTab.set('profile');
        // Deliberately not remembered: re-doing the gesture costs two seconds,
        // and once the setting is ON the section renders unconditionally, so
        // the only people who ever repeat it are those who declined.
        this._profileClicks.set(0);
        this._developerModeRevealed.set(false);
    }
}
